/**
 * Default filter/dialog options served immediately (and as a resilient
 * fallback if the constants query fails), then refined from the API in
 * useInventoryConstants. Not test data — these keep filters and dialogs
 * usable before/without a successful constants fetch.
 */
import type { ConstantOption, InventoryConstants } from './types';

export const DEFAULT_STATES: InventoryConstants['states'] = [
  { code: 'CREATED', displayName: 'Created' },
  { code: 'CATALOG_REVIEW', displayName: 'In Review for Catalog' },
  { code: 'FORSALE_REVIEW', displayName: 'In Review for Sale' },
  { code: 'FORSALE_APPROVED', displayName: 'Approved for Sale' },
  { code: 'FORSALE_UNLISTED', displayName: 'Approved but Unlisted' },
  { code: 'FORSALE_REJECTED', displayName: 'Rejected for Sale' },
  { code: 'PARTNER_REJECTED', displayName: 'Rejected for Partner' },
  { code: 'NEEDS_CLEANING', displayName: 'Needs Cleaning' },
  { code: 'CLEANING_IN_PROGRESS', displayName: 'Cleaning in Progress' },
  { code: 'NEEDS_REPAIR', displayName: 'Needs Repair' },
  { code: 'REPAIR_IN_PROGRESS', displayName: 'Repair in Progress' },
  { code: 'PHOTOGRAPHY_PENDING', displayName: 'Photography Pending' },
  { code: 'PHOTOGRAPHY_IN_PROGRESS', displayName: 'Photography in Progress' },
  { code: 'ORDERED', displayName: 'Ordered' },
  { code: 'PICKED', displayName: 'Picked' },
  { code: 'PACKED', displayName: 'Packed' },
  { code: 'SHIPPED', displayName: 'Shipped' },
  { code: 'SENDBACK_PENDING', displayName: 'Pending Sendback' },
  { code: 'MISSING', displayName: 'Missing' },
  { code: 'DAMAGED', displayName: 'Damaged' },
];

export const DEFAULT_CONDITIONS: InventoryConstants['conditions'] = [
  { code: 'GRADE_A', displayName: 'Like New' },
  { code: 'GRADE_B', displayName: 'Excellent' },
  { code: 'GRADE_C', displayName: 'Good' },
  { code: 'GRADE_D', displayName: 'Fair' },
];

export const DEFAULT_REJECT_REASONS: InventoryConstants['rejectReasons'] = [
  { code: 'DAMAGED', displayName: 'Damaged beyond repair' },
  { code: 'STAINED', displayName: 'Stained' },
  { code: 'NOT_AUTHENTIC', displayName: 'Could not authenticate' },
  { code: 'OUT_OF_SCOPE', displayName: 'Outside program scope' },
];

/**
 * Storefront availability resolves to a CatalogItem scope
 * (models/catalog_item.js). The common scopes only — the full set also has
 * INELIGIBLE_RETURN / DESTROY / BRAND / PROP65 / PRICE, which the live
 * constants query supplies once it loads.
 */
export const DEFAULT_AVAILABILITIES: InventoryConstants['availabilities'] = [
  { code: 'ELIGIBLE', displayName: 'Eligible' },
  { code: 'PROVISIONAL', displayName: 'Provisional' },
  { code: 'INELIGIBLE', displayName: 'Ineligible' },
];

/**
 * Check-in availability is a plain column filter, not a scope — same codes,
 * different mechanism. Omits DISQUALIFIED, which the live query supplies.
 */
export const DEFAULT_CHECKIN_AVAILABILITIES: InventoryConstants['checkinAvailabilities'] = [
  { code: 'ELIGIBLE', displayName: 'Eligible' },
  { code: 'PROVISIONAL', displayName: 'Provisional' },
  { code: 'INELIGIBLE', displayName: 'Ineligible' },
];

/**
 * reware-api CUSTODY_STATUSES, which has no constants endpoint (reware-admin
 * hardcodes it too). DELETED is deliberately omitted: filtering on it drops
 * the ALL state scope (which hides DELETED items) and surfaces deleted items.
 */
export const CUSTODY_STATUSES: ConstantOption[] = [
  { code: 'ON_HAND', displayName: 'On hand' },
  { code: 'ALLOCATED', displayName: 'Allocated' },
  { code: 'IN_TRANSIT', displayName: 'In transit' },
  { code: 'AT_STORE', displayName: 'At store' },
  { code: 'AT_VENDOR', displayName: 'At vendor' },
  { code: 'EXITED', displayName: 'Exited' },
  { code: 'MISSING', displayName: 'Missing' },
  { code: 'SHRINKAGE', displayName: 'Shrinkage' },
];

/**
 * reware-api VasJob.STATUSES, matched against an item's latest
 * repair/cleaning/photography job. FAILED is omitted: reware-api derives the
 * status from the job's started/completed/declined timestamps only, so a
 * FAILED query returns in-progress jobs.
 */
export const VAS_STATUSES: ConstantOption[] = [
  { code: 'STAGED', displayName: 'Staged' },
  { code: 'IN_PROGRESS', displayName: 'In progress' },
  { code: 'COMPLETED', displayName: 'Completed' },
  { code: 'DECLINED', displayName: 'Declined' },
];

/**
 * isFlagged, needsPhotography, and locationId are declared by reware-api's
 * inventoryItems query but never read by its resolver, so their filters would
 * silently do nothing. They're wired end to end behind this flag.
 *
 * Nothing fails if reware-api starts honouring them — the filters just stay
 * hidden — so this needs a deliberate recheck rather than waiting for a test to
 * go red. Verify against the `inventoryItems` resolver in
 * reware-api/lib/graphql/admin/queries/index.js before flipping.
 */
export const INERT_FILTERS_ENABLED = false;

/** Values of the combined "Job status" filter's type half → reware-api argument. */
export const VAS_JOB_TYPES = [
  { code: 'repairStatus', displayName: 'Repair' },
  { code: 'cleaningStatus', displayName: 'Cleaning' },
  { code: 'photographyStatus', displayName: 'Photography' },
] as const;
